import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, TextField, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FastField, FormikProps } from "formik";
import DropdownSelector from "../../atoms/Common/DropdownSelector.tsx";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getCurrencyList } from "../../api/common/currencies.js";
import { getClientList } from "../../api/client/index.js";
import {
  createQuotationHeader,
  getQuotationById,
  updateQuotation,
} from "../../api/quotations/index.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { useCompany } from "../../contexts/CompanyContext";

interface Client {
  id: string;
  name: string;
  code: string;
}

interface QuotingFormHeaderProps {
  onSubmit: (data: QuotingFormData) => void;
  id?: number;
}

export interface QuotingFormData {
  client?: Client;
  name: string;
  currencyId: string;
}

const QuotingFormHeader: React.FC<QuotingFormHeaderProps> = ({
  onSubmit,
  id,
}) => {
  const navigate = useNavigate();
  const { openSnack } = useSnack();
  const { selectedCompany } = useCompany();
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<QuotingFormData>({
    name: "",
    currencyId: "",
  });

  // Memoize the fetch function to prevent infinite re-renders
  const memoizedGetClientList = useCallback(getClientList, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getQuotationById(id);
      const data = response.data;
      setFormData({
        client: data.client,
        name: data.name,
        currencyId: data.currencyId,
      });
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        name: "",
        currencyId: "",
      });
      setLoading(false);
    }
  }, [id, formMode]);

  const handleSubmit = async (values: QuotingFormData) => {
    let payload = {
      ...values,
      clientId: values.client?.id,
      companyId: selectedCompany?.id,
    };
    try {
      let quoteData;
      if (id) {
        const response = await updateQuotation(id, payload);
        quoteData = response.data;
        openSnack("Cotización actualizada correctamente", "success");
      } else {
        const response = await createQuotationHeader(payload);
        quoteData = response.data;
      }
      console.log(quoteData);

      navigate("/quotingDetail", { state: quoteData });
      onSubmit(values);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  // if (loading) {
  //   return <CircularProgress />;
  // }

  return (
    <Formik
      initialValues={formData}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {(formikProps: FormikProps<QuotingFormData>) => (
        <Form>
          <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", p: 2 }}>
            <LGAutocomplete
              fetchFunction={memoizedGetClientList}
              optionLabel="code"
              onChange={(_, value) => {
                formikProps.setFieldValue("client", value);
              }}
              label="Cliente"
            />

            <FastField
              name="name"
              as={TextField}
              fullWidth
              label="Nombre de la cotización"
              margin="normal"
              required
            />

            <FastField
              name="currencyId"
              as={DropdownSelector}
              fetchFunction={getCurrencyList}
              label="Moneda"
              optionLabel={["symbol", "name"]}
              optionSeparator=" | "
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  !formikProps.values.name || !formikProps.values.currencyId
                }
              >
                {formMode === "edit" ? "Actualizar" : "Siguiente"}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default QuotingFormHeader;
